// Core modules
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Application components
import { AuthService } from '@core/services/auth.service';
import { RequestService } from '@core/services/request.service';
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component';
import { LoginComponent } from '../login/login.component';
import { SignUpComponent } from '../sign-up/sign-up.component';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { CampaignModel } from '@app/shared/models/campaign.model';
import { LanguageService } from '@app/core/services/language.service';
import { CountryConfig, LanguageOption, LocationService } from '@app/core/services/location.service';

/**
 * Header component
 */
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  public campaign: CampaignModel;
  private destroy$ = new Subject<void>();

  public username: string;
  public email: string;
  languages: LanguageOption[] = [];
  countries: CountryConfig[] = [];
  selectedLanguage = 'nl';
  selectedCountry = 'NL';

  constructor(
    public dialog: MatDialog,
    public authService: AuthService,
    private requestService: RequestService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private languageService: LanguageService,
    private locationService: LocationService,
    private cookieService: CookieService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.countries = this.locationService.supportedCountries;
  }

  /**
   * Life cycle init method
   */
  ngOnInit(): void {
    this.locationService.localeState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.selectedCountry = state.country;
        this.selectedLanguage = state.language;
        this.languages = state.languages;
      });

    this.authService.getUsername()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.username = data ? data : '';
      });

    this.authService.getCampaign()
      .pipe(takeUntil(this.destroy$))
      .subscribe((campaign) => {
        this.campaign = campaign;
        if (this.campaign && this.campaign.countryId) {
          const campaignCountry = this.locationService.getCountryCodeById(this.campaign.countryId);
          if (campaignCountry && this.selectedCountry !== campaignCountry) {
            const campaignLang = this.campaign.languageId
              ? this.languageService.getLanguageCode(this.campaign.languageId)
              : undefined;
            this.locationService.setCountry(campaignCountry, false, campaignLang);
          }
        }
      });

    this.updateAuthenticationHeader();
    this.authService.getLoginDialog()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.openLoginDialog(data));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectedCountryObject(): CountryConfig {
    return this.countries.find((c) => c.value === this.selectedCountry) || this.countries[0];
  }

  selectedLanguageObject(): LanguageOption {
    return this.languages.find((l) => l.value === this.selectedLanguage) || this.languages[0];
  }
  
  updateAuthenticationHeader() {
    if (this.authService.isAuthenticated()) {
      this.authService.getUser().subscribe(
        (data) => this.authService.setUsername(data.firstName ?? data.email),
        (error)=> this.authService.logout());
    }
  }

  /**
   * Open login dialog
   */
  openLoginDialog(campaignData?): void {
    const dialogRef = this.dialog.open(LoginComponent, {
      width: '568px',
      backdropClass: 'kbk-backdrop',
      data: { email: this.email},
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data?.email) {
        this.email = data.email;
      }
      if (data?.event === 'signUp') {
        this.openSignUpDialog();
      } else if (data?.event === 'forgotPassword') {
        this.openForgotPasswordDialog();
      } else if (data?.event === 'login') {
        this.updateAuthenticationHeader();
      }
      // Handling for campaign details page
      if (campaignData && campaignData.campaignId) {
        this.authService.getUserInfo().subscribe((user) => {
          if (user!=null){ 
            this.requestService.checkDuplicateEmail(campaignData.campaignId, user.email).subscribe((result) => {
              if (!result) {
                this.router.navigate([`campaigns/${campaignData.urlPath}/${campaignData.campaignId}/upload`],
                  { queryParams: this.route.snapshot.queryParams });
              } else {
                this.snackBar.open(this.translateService.instant('DUPLICATE_USER_REQUEST'), 'X', { duration: 3000 });
              }
            });
          }
        });
      }
    });
  }

  /**
   * Open sign up dialog
   */
  openSignUpDialog(): void {
    const dialogRef = this.dialog.open(SignUpComponent, {
      width: '568px',
      backdropClass: 'kbk-backdrop',
      data: { email: this.email, lang: this.selectedLanguage + '-' + this.selectedCountry },
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data === 'login') {
        this.openLoginDialog();
      }
    });
  }

  openForgotPasswordDialog(): void {
    const dialogRef = this.dialog.open(ForgotPasswordComponent, {
      width: '568px',
      backdropClass: 'kbk-backdrop',
      data: { email: this.email, lang: this.selectedLanguage + '-' + this.selectedCountry },
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data === 'signUp') {
        this.openSignUpDialog();
      } else if (data === 'login') {
        this.openLoginDialog();
      }
    });
  }

  /**
   * Logged out the user and navigate to home
   */
  logout(): void {
    this.authService.logout();
    this.router.navigate(['']);
  }

  onCountryChange(selectedCountry: string) {
    this.locationService.setCountry(selectedCountry, true);
    this.syncQueryParams();
  }

  onLanguageChange(selectedLanguage: string) {
    this.locationService.setLanguage(selectedLanguage, true);
    this.syncQueryParams();
  }

  private syncQueryParams() {
    const state = this.locationService.currentLocale;
    const currentParams = this.route.snapshot.queryParams;
    const currentLang = currentParams?.lang;
    const currentLangId = currentParams?.languageId;
    const targetLangId = this.languageService.getLanguageId(state.language);

    if (currentLang === state.langCode && String(currentLangId) === String(targetLangId)) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        lang: state.langCode,
        languageId: targetLangId
      },
      queryParamsHandling: 'merge'
    });
  }
}
